/**
 * FILE: packages/supabase/src/services/notifications.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { logActivity, withActivityActor, type ActivityActor } from '../internal/activity'
import { ServiceError } from '../internal/errors'
import type { Database } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";
import { isDemoToken, studentPortalHeaders, type StudentPortalContext } from '../internal/studentPortal'
import { demoNotifications } from './demoData'

type NotificationRow = Database['public']['Tables']['notifications']['Row']
type NotificationInsert = Database['public']['Tables']['notifications']['Insert']

export type HubNotificationType = 'alert' | 'info' | 'success' | 'broadcast'

export interface CreateNotificationPayload {
  title: string
  message: string
  type: HubNotificationType
  for_user_id?: string | null
  for_role?: string | null
  for_phone?: string | null
  entity_type?: string | null
  entity_id?: string | null
  action_url?: string | null
}

export type StaffNotification = NotificationRow

function notificationScopeOrFilter(staffId: string, roleName: string | undefined): string | null {
  if (roleName) {
    return `for_user_id.eq.${staffId},for_role.eq.${roleName}`
  }
  return null
}

/** Recent notifications visible to the staff member (by user id or role). */
export async function getNotifications(
  staffId: string,
  opts?: {
        unreadOnly?: boolean
        limit?: number
        roleName?: string
      }, client: DbClient = defaultDbClient
): Promise<StaffNotification[]> {
  const limit = opts?.limit ?? 50
  const scope = notificationScopeOrFilter(staffId, opts?.roleName)

  let q = client.from('notifications').select('*').order('created_at', { ascending: false }).limit(limit)

  if (scope) {
    q = q.or(scope)
  } else {
    q = q.eq('for_user_id', staffId)
  }

  if (opts?.unreadOnly) {
    q = q.eq('read', false)
  }

  const { data, error } = await q
  if (error) throw new ServiceError(error.message, 'NOTIFICATIONS_LIST', error)
  return data ?? []
}

export async function markAsRead(notificationId: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { data: row, error: fetchErr } = await client
    .from('notifications')
    .select('id, title, read')
    .eq('id', notificationId)
    .maybeSingle()

  if (fetchErr) throw new ServiceError(fetchErr.message, 'NOTIFICATION_FETCH', fetchErr)
  if (!row) throw new ServiceError('Notification not found', 'NOTIFICATION_NOT_FOUND')

  const { error } = await client
    .from('notifications')
    .update({ read: true, read_at: new Date().toISOString() })
    .eq('id', notificationId)

  if (error) throw new ServiceError(error.message, 'NOTIFICATION_MARK_READ', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'NOTIFICATION_READ',
      entityType: 'notifications',
      entityId: notificationId,
      oldValues: { read: row.read },
      newValues: { read: true },
    }), client
  )
}

export async function markAllAsRead(staffId: string, roleName: string | undefined, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const now = new Date().toISOString()
  const scope = notificationScopeOrFilter(staffId, roleName)

  let q = client.from('notifications').update({ read: true, read_at: now }).eq('read', false)
  if (scope) {
    q = q.or(scope)
  } else {
    q = q.eq('for_user_id', staffId)
  }

  const { error } = await q
  if (error) throw new ServiceError(error.message, 'NOTIFICATIONS_MARK_ALL', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'NOTIFICATION_MARK_ALL_READ',
      entityType: 'notifications',
      entityId: staffId,
      newValues: { roleName: roleName ?? null, at: now },
    }), client
  )
}

export async function createNotification(payload: CreateNotificationPayload, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<StaffNotification> {
  if (!payload.for_user_id && !payload.for_role && !payload.for_phone) {
    throw new ServiceError('Notification must target for_user_id, for_role, or for_phone', 'NOTIFICATION_NO_TARGET')
  }

  const insert: NotificationInsert = {
    title: payload.title,
    message: payload.message,
    type: payload.type,
    read: false,
    channel: 'in_app',
    for_user_id: payload.for_user_id ?? null,
    for_role: payload.for_role ?? null,
    for_phone: payload.for_phone ?? null,
    entity_type: payload.entity_type ?? null,
    entity_id: payload.entity_id ?? null,
    action_url: payload.action_url ?? null,
  }

  const { data, error } = await client.from('notifications').insert(insert).select().single()
  if (error) throw new ServiceError(error.message, 'NOTIFICATION_CREATE', error)
  if (!data) throw new ServiceError('No row returned', 'NOTIFICATION_CREATE')

  await logActivity(
    withActivityActor(actor, {
      action: 'NOTIFICATION_CREATE',
      entityType: 'notifications',
      entityId: data.id,
      newValues: {
        title: payload.title,
        type: payload.type,
        for_user_id: payload.for_user_id,
        for_role: payload.for_role,
        for_phone: payload.for_phone,
      },
    }), client
  )

  return data
}

/* ───────── Student-facing in-app notifications (edge functions) ───────── */

export interface StudentInAppNotification {
  id: string
  title: string | null
  message: string | null
  body: string | null
  type: string | null
  read: boolean | null
  action_url: string | null
  entity_type: string | null
  entity_id: string | null
  created_at: string | null
}

export async function getStudentInAppNotifications(
  ctx: StudentPortalContext,
): Promise<StudentInAppNotification[]> {
  if (isDemoToken(ctx.token)) {
    return demoNotifications.map((n) => ({
      id: n.id,
      title: n.title,
      message: n.body,
      body: n.body,
      type: 'system',
      entity_type: 'system',
      entity_id: n.id,
      action_url: null,
      read: n.is_read,
      created_at: n.created_at,
    })) as StudentInAppNotification[]
  }
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/student-chat?action=notifications`, {
    headers: studentPortalHeaders(ctx),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to fetch notifications' }))
    throw new ServiceError(err.error, 'STUDENT_NOTIFICATIONS_FETCH')
  }
  const data = (await res.json()) as { notifications: StudentInAppNotification[] }
  return data.notifications ?? []
}

export async function markStudentInAppNotificationRead(
  notificationId: string,
  ctx: StudentPortalContext,
): Promise<void> {
  if (isDemoToken(ctx.token)) return
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/student-chat?action=notification_read`, {
    method: 'POST',
    headers: studentPortalHeaders(ctx),
    body: JSON.stringify({ notificationId }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to mark read' }))
    throw new ServiceError(err.error, 'STUDENT_NOTIFICATION_READ')
  }
}
